import { Document, BLOCKS, MARKS } from '@contentful/rich-text-types';
import { Entry, EntryFields } from 'contentful';
import { TypeBackgroundColorFields } from '@/core/generated-types';

/** Rich text content - accepts Contentful RichText, Document, or plain string */
type RichTextInput =
  | string
  | Document
  | EntryFields.RichText
  | undefined
  | null;

/**
 * Extracts text content from a Rich Text Document node.
 */
function extractText(node: any): string {
  if (node.nodeType === 'text') return node.value || '';
  if (node.content) return node.content.map(extractText).join('');
  return '';
}

/**
 * Normalizes content to a Rich Text Document.
 * Accepts plain strings, Contentful RichText, or Document types.
 * Returns undefined for empty, whitespace-only, null, or undefined input.
 *
 * @param content - String, Contentful RichText, or Document
 * @returns Rich Text Document if content exists, undefined otherwise
 */
export function normalizeToRichText(
  content: RichTextInput,
): Document | undefined {
  if (content === null || content === undefined) {
    return undefined;
  }

  // Handle plain string
  if (typeof content === 'string') {
    if (content.trim().length === 0) {
      return undefined;
    }
    return {
      nodeType: BLOCKS.DOCUMENT,
      data: {},
      content: [
        {
          nodeType: BLOCKS.PARAGRAPH,
          data: {},
          content: [
            {
              nodeType: 'text',
              value: content,
              marks: [] as { type: (typeof MARKS)[keyof typeof MARKS] }[],
              data: {},
            },
          ],
        },
      ],
    };
  }

  // Handle document - check if it has content
  if (typeof content === 'object' && content.nodeType === 'document') {
    if (!content.content || extractText(content).trim().length === 0) {
      return undefined;
    }
    return content as Document;
  }

  return undefined;
}

/**
 * Checks if a string has content after trimming.
 * Returns false for empty strings, whitespace-only strings, or null/undefined.
 *
 * @param value - The string to check
 * @returns true if the string contains non-whitespace content
 */
export function hasStringContent(value: string | undefined | null): boolean {
  return Boolean(value?.trim());
}

/**
 * Converts a color string to the Entry<TypeBackgroundColorFields> format
 * that MessageBanner expects.
 *
 * @param color - Color string (e.g., 'white', 'blue-deep') or undefined
 * @returns Entry format if color exists, undefined otherwise
 */
export function colorStringToEntry(
  color: string | undefined | null,
): Entry<TypeBackgroundColorFields> | undefined {
  return color
    ? ({ fields: { color } } as Entry<TypeBackgroundColorFields>)
    : undefined;
}
